import { Ref, ref } from "vue";
import {
  FileUploadCallbackFunctionType,
  TextEditorFileExtension,
} from "../types";
import {
  TirPmNotificationEventBus,
  TirPmNotificationModel,
} from "tir-pm-notification";
import { videoExtensions } from "../constants";

type FileManagerOptions = {
  /**Ссылка на редактор */
  $editor: Ref<HTMLElement>;
  /**Группа для уведомлений */
  notificationGroup: string;
  /**Коллбек для загрузки файла */
  fileUploadCallbak?: FileUploadCallbackFunctionType;
  /**Максимальный размер загружаемого файла */
  maxFileSize: number;
  /**Допустимые расширения файла */
  allowedFileExtensions: Array<TextEditorFileExtension>;
  /**Обновить значение */
  updateValue: () => any;
};

export const useFileManager = (options: FileManagerOptions) => {
  /**Загрузка файла */
  const isLoadingFile = ref(false);
  /**При дропе чего-либо в область редактора */
  const onDrop = (event: DragEvent) => {
    event.preventDefault(); // Предотвращаем стандартное поведение
    const selection = window.getSelection();

    // Проверка наличия выделения
    if (!(selection && selection.rangeCount > 0)) {
      options.$editor.value?.focus();
      if (!(selection && selection.rangeCount > 0)) return;
    }

    // Получаем координаты дропа
    const x = event.clientX;
    const y = event.clientY;

    // Получаем элемент редактора
    const editor = options.$editor.value;
    const range = document.createRange();
    const position = getCaretPositionFromMouse(editor, x, y);
    console.log(position);
    range.setStart(position.node, position.offset);
    range.collapse(true);

    // Проверяем, есть ли файлы дропа
    if (!event.dataTransfer?.files?.length) return;
    const file = event.dataTransfer.files[0];
    pasteFile(file, range);
  };

  /**Получение узла и смещения для установки каретки по координатам мыши */
  const getCaretPositionFromMouse = (
    element: HTMLElement,
    x: number,
    y: number
  ) => {
    let range;

    // Проверяем, какой метод доступен
    if (typeof document.caretRangeFromPoint === "function") {
      range = document.caretRangeFromPoint(x, y);
      // @ts-ignore
    } else if (typeof document.caretPositionFromPoint === "function") {
      // @ts-ignore
      const position = document.caretPositionFromPoint(x, y);
      if (position) {
        return {
          node: position.offsetNode,
          offset: position.offset,
        };
      }
    }

    // Если используется caretRangeFromPoint
    if (range) {
      return {
        node: range.startContainer,
        offset: range.startOffset,
      };
    }

    // Если не удалось найти позицию каретки
    return { node: element, offset: 0 };
  };

  /**Проверить разрешена ли загрузка файла */
  const checkAllowUploadFile = (file: File) => {
    const ext = file.name.split(".").at(-1);
    const isVideoFile = videoExtensions.some((videoExt) => videoExt === ext);
    // Разрешена ли загрузка файла с таким расширением
    if (!options.allowedFileExtensions.some((allowExt) => allowExt === ext)) {
      TirPmNotificationEventBus.emit(
        "add",
        new TirPmNotificationModel({
          title: "Расширение загружаемого файла не поддерживается",
          type: "error",
          group: options.notificationGroup,
          life: 5000,
          closable: true,
        })
      );
      return false;
    }
    // Проверка на максимальный размер загружаемого файла
    if (file.size > options.maxFileSize && !isVideoFile) {
      TirPmNotificationEventBus.emit(
        "add",
        new TirPmNotificationModel({
          title: "Большой размер файла",
          type: "error",
          group: options.notificationGroup,
          life: 5000,
          closable: true,
        })
      );
      return false;
    }

    return true;
  };

  /**Вставить файл */
  const pasteFile = async (file: File, range: Range) => {
    if (!options.fileUploadCallbak) return;
    if (!checkAllowUploadFile(file)) return;

    isLoadingFile.value = true;
    await options.fileUploadCallbak(file, async (slotId: string | number) => {
      try {
        
        const el = document.createElement("span");
        el.contentEditable = "false";
        el.setAttribute("data-widget-id", slotId.toString());
        el.setAttribute("data-file-id", slotId.toString());
  
        // Определяем родителя текущего узла
        const parentElement =
          (
            (range.commonAncestorContainer.nodeType === 3
              ? range.commonAncestorContainer.parentElement
              : range.commonAncestorContainer) as HTMLElement
          ).closest(
            ".tir-pm-text-editor-file-input-area__content div:not([data-widget])"
          ) ?? options.$editor.value;
  
        console.log("parentElement", parentElement);
        // Разделяем содержимое родителя на две части: до каретки и после
        const beforeRange = range.cloneRange();
        beforeRange.setStart(parentElement, 0); // От начала родителя до каретки
        const afterRange = range.cloneRange();
        afterRange.setEnd(parentElement, parentElement.childNodes.length); // После каретки до конца
  
        // Получаем фрагменты содержимого до и после каретки
        const beforeContent = beforeRange.extractContents();
        const afterContent = afterRange.extractContents();
  
        // Очищаем содержимое родителя
        (parentElement as any).innerHTML = "";
        parentElement.appendChild(beforeContent);
  
        // Вставляем новый элемент на уровень родителя
        parentElement.appendChild(el);
  
        // Вставляем оставшееся содержимое после нового элемента
        parentElement.appendChild(afterContent);
  
        // Обновляем значение после вставки
        await options.updateValue();
  
        // Устанавливаем каретку после вставленного элемента
        const newRange = document.createRange();
        newRange.setStartAfter(el);
        newRange.collapse(true);
      } catch (e) {
        console.error(e);
      }
      isLoadingFile.value = false;
    });
  };

  return {
    onDrop,
    pasteFile,
    isLoadingFile,
  };
};
